import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchGenres } from '../api/tmdbApi.js';
import useMovieSearch from '../hooks/useMovieSearch.js';
import useActorDetails from '../hooks/useActorDetails.js';

const MovieContext = createContext();

export const useMovieContext = () => {
  const context = useContext(MovieContext);
  if (!context) {
    throw new Error('useMovieContext must be used within MovieProvider');
  }
  return context;
};

export const MovieProvider = ({ children, showSnackbar }) => {
  // Genres state
  const [genresMap, setGenresMap] = useState({});

  // Movie selection state
  const [selectedMovie, setSelectedMovie] = useState(null);

  // Movie search hook
  const movieSearch = useMovieSearch(genresMap, showSnackbar);

  // Actor details hook
  const actorDetails = useActorDetails(genresMap, showSnackbar);

  // Load genres on mount
  useEffect(() => {
    const loadGenres = async () => {
      const { data, error } = await fetchGenres();

      if (error) {
        showSnackbar(error, 'error');
        return;
      }

      setGenresMap(data);
    };

    loadGenres();
  }, [showSnackbar]);

  // After genres are fetched, fetch initial popular movies
  useEffect(() => {
    if (Object.keys(genresMap).length > 0) {
      movieSearch.fetchInitialMovies();
      if (movieSearch.searchInputRef.current) {
        movieSearch.searchInputRef.current.focus();
      }
    }
  }, [genresMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    // Genres
    genresMap,

    // Movie selection
    selectedMovie,
    setSelectedMovie,

    // Movie search (from useMovieSearch hook)
    searchQuery: movieSearch.searchQuery,
    activeSearchTerm: movieSearch.activeSearchTerm,
    selectedGenre: movieSearch.selectedGenre,
    searchResults: movieSearch.searchResults,
    isLoadingMovies: movieSearch.isLoadingMovies,
    isLoadingMore: movieSearch.isLoadingMore,
    movieFetchError: movieSearch.movieFetchError,
    currentPage: movieSearch.currentPage,
    totalPages: movieSearch.totalPages,
    searchInputRef: movieSearch.searchInputRef,
    fetchInitialMovies: movieSearch.fetchInitialMovies,
    handleSearchChange: movieSearch.handleSearchChange,
    handleGenreChange: movieSearch.handleGenreChange,
    handleSearchSubmit: movieSearch.handleSearchSubmit,
    handleLoadMoreMovies: movieSearch.handleLoadMore,

    // Actor details (from useActorDetails hook)
    selectedActor: actorDetails.selectedActor,
    actorBiography: actorDetails.actorBiography,
    actorMovies: actorDetails.actorMovies,
    isLoadingActorDetails: actorDetails.isLoadingActorDetails,
    isLoadingActorMovies: actorDetails.isLoadingActorMovies,
    actorDetailsError: actorDetails.actorDetailsError,
    actorMoviesCurrentPage: actorDetails.actorMoviesCurrentPage,
    hasMoreActorMovies: actorDetails.hasMoreActorMovies,
    fetchActorDetails: actorDetails.fetchActorDetails,
    handleLoadMoreActorMovies: actorDetails.handleLoadMoreActorMovies,
    setSelectedActor: actorDetails.setSelectedActor,
  };

  return (
    <MovieContext.Provider value={value}>
      {children}
    </MovieContext.Provider>
  );
};
